import { useEffect, useRef, useState, type ReactNode } from "react";
import { Copy } from "lucide-react";

// 底部提示（toast）：成功了但界面上看不出来时给一句（DESIGN.md §4.17），3 秒消失。
// 出错不用它——出错写在页内提示条里，不自动消失。

const EVENT = "diag:toast";

export function toast(message: string) {
  window.dispatchEvent(new CustomEvent<string>(EVENT, { detail: message }));
}

export function ToastZone() {
  const [items, setItems] = useState<{ id: number; text: string }[]>([]);
  const seq = useRef(0);
  useEffect(() => {
    const on = (e: Event) => {
      const id = ++seq.current;
      setItems((xs) => [...xs, { id, text: (e as CustomEvent<string>).detail }]);
      setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 3000);
    };
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  return (
    <div className="toast-zone" role="status" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className="toast"><span>{t.text}</span></div>
      ))}
    </div>
  );
}

function useCopyText() {
  const [error, setError] = useState<string | null>(null);
  const copyText = async (text: string, label?: string) => {
    setError(null);
    try {
      await navigator.clipboard.writeText(text);
      toast(label ? `已复制 ${label}` : "已复制");
    } catch {
      setError("复制没有成功：浏览器不允许读写剪贴板，请手动选中复制，或再试一次。");
    }
  };
  return { copyText, error };
}

/** 跟在编号后面的「复制」小链接（SN、请求编号）。复制后提示带上值 */
export function CopyLink({ value, label = "复制" }: { value: string; label?: string }) {
  const { copyText, error } = useCopyText();
  return (
    <>
      <button
        type="button"
        className="copybtn"
        onClick={(e) => {
          e.stopPropagation();
          void copyText(value, value);
        }}
      >
        <Copy className="ico" aria-hidden="true" />
        {label}
      </button>
      {error && <span className="err" role="alert">{error}</span>}
    </>
  );
}

/** 页头里的「复制 SN」这类白底按钮 */
export function CopyButton({ value, children, toastLabel }: {
  value: string;
  children: ReactNode;
  toastLabel?: string;
}) {
  const { copyText, error } = useCopyText();
  return (
    <>
      <button type="button" className="btn" onClick={() => void copyText(value, toastLabel ?? value)}>
        <Copy className="ico" aria-hidden="true" />
        {children}
      </button>
      {error && <span className="err" role="alert">{error}</span>}
    </>
  );
}
